import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActionsBar from '../components/shared/DialogActionsBar';
import { SECONDARY_BUTTON_SX } from '../components/shared/dialogActionsSx';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTourStore } from '../stores/tourStore';
import { useGlobalStore } from '../stores/globalStore';
import { ALL_TOURS } from './tourDefinitions';

/**
 * Dialog that lists all available tours. Users can pick one to start,
 * see which ones they've completed, and opt out of auto-prompts.
 */
export default function TourListDialog() {
  const navigate = useNavigate();
  const location = useLocation();
  const tourListOpen = useTourStore((s) => s.tourListOpen);
  const closeTourList = useTourStore((s) => s.closeTourList);
  const completedTours = useTourStore((s) => s.completedTours);
  const toursDisabled = useTourStore((s) => s.toursDisabled);
  const setToursDisabled = useTourStore((s) => s.setToursDisabled);
  const startTour = useTourStore((s) => s.startTour);
  const resetAllTours = useTourStore((s) => s.resetAllTours);
  const userId = useGlobalStore((s) => s.userId);

  const handleStartTour = (tourId: string, startRoute: string | null) => {
    // Navigate to the tour's start route if we're not already there
    if (startRoute && !location.pathname.startsWith(startRoute)) {
      navigate(startRoute);
      // Wait for navigation before starting tour
      setTimeout(() => {
        startTour(tourId as any);
      }, 500);
    } else {
      startTour(tourId as any);
    }
  };

  return (
    <Dialog
      open={tourListOpen}
      onClose={closeTourList}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: 'tour-list-dialog' }}
    >
      <DialogTitle sx={{ pb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <i className="bi bi-mortarboard" style={{ fontSize: 'var(--font-size-large)', color: 'var(--primary-color)' }} />
        Guided Tours
      </DialogTitle>

      <DialogContent sx={{ pt: 1.5 }}>
        <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--text-secondary)', marginTop: 0, marginBottom: 16 }}>
          Choose a tour to learn about different areas of the application. Tours guide you through the interface step by step.
        </p>

        {ALL_TOURS.map((tour) => {
          const isCompleted = completedTours.has(tour.id);
          return (
            <div
              key={tour.id}
              className="tour-list-item"
              onClick={() => handleStartTour(tour.id, tour.startRoute)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleStartTour(tour.id, tour.startRoute);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={`Start ${tour.label} tour${isCompleted ? ' (completed)' : ''}`}
            >
              <div className="tour-list-icon">
                <i className={`bi ${tour.icon}`} />
              </div>
              <div className="tour-list-info">
                <div className="tour-list-label">{tour.label}</div>
                <div className="tour-list-description">{tour.description}</div>
              </div>
              <span className={`tour-list-badge ${isCompleted ? 'completed' : 'new'}`}>
                {isCompleted ? 'Completed' : 'New'}
              </span>
            </div>
          );
        })}

        <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border-color)' }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={toursDisabled}
                onChange={(e) => setToursDisabled(e.target.checked, userId)}
              />
            }
            label={
              <span style={{ fontSize: 'var(--font-size-base)', color: 'var(--text-secondary)' }}>
                Don't show tour prompts automatically
              </span>
            }
          />
        </div>
      </DialogContent>

      <DialogActionsBar
        onCancel={closeTourList}
        cancelLabel="Close"
        leftSlot={
          <Button
            variant="outlined"
            sx={SECONDARY_BUTTON_SX}
            onClick={() => { resetAllTours(userId); }}
          >
            Reset All Progress
          </Button>
        }
      />
    </Dialog>
  );
}
